import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, Download, ExternalLink, Microscope, FileText, Youtube } from "lucide-react";
import { RESEARCH, type IngredientResearch } from "@/lib/research";
import { cn } from "@/lib/utils";
import { trackSocialClick } from "@/components/SocialLinks";

const SKU_DOT: Record<IngredientResearch["sku"], string> = {
  mag: "bg-[--sku-mag]",
  liver: "bg-[--sku-liver]",
  ova: "bg-[--sku-ova]",
};

export function ResearchDrawer({ children, defaultSlug }: { children: React.ReactNode; defaultSlug?: string }) {
  const [active, setActive] = useState<string>(defaultSlug ?? RESEARCH[0].slug);
  const ingredient = RESEARCH.find((r) => r.slug === active) ?? RESEARCH[0];

  return (
    <Sheet>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full overflow-hidden p-0 sm:max-w-2xl md:max-w-3xl"
      >
        <div className="flex h-full flex-col bg-white">
          <SheetHeader className="border-b border-border px-6 pb-5 pt-6 text-left md:px-8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
              Clinical Library
            </p>
            <SheetTitle className="mt-1 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              10,000+ Studies. Open the receipts.
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Each TruWe active is grounded in peer-reviewed research. Browse the leading scientists,
              click through to PubMed, or download the one-page brief.
            </SheetDescription>
            <a
              href="https://youtube.com/@truwefuturenutrition?si=4ddsaBZyjCjD8sKS"
              target="_blank"
              rel="noreferrer"
              aria-label="Watch TruWe on YouTube"
              data-analytics="social_click"
              data-social-network="youtube"
              onClick={() =>
                trackSocialClick("YouTube", "https://youtube.com/@truwefuturenutrition", "library")
              }
              onAuxClick={() =>
                trackSocialClick("YouTube", "https://youtube.com/@truwefuturenutrition", "library")
              }
              className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-[#FF0000]/20 bg-[#FF0000]/5 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#FF0000] transition-all hover:-translate-y-0.5 hover:bg-[#FF0000] hover:text-white hover:shadow-[0_8px_24px_-8px_rgba(255,0,0,0.35)]"
            >
              <Youtube className="h-4 w-4" />
              Watch nutrition explainers on YouTube
            </a>
          </SheetHeader>

          <div className="grid flex-1 grid-cols-1 overflow-hidden md:grid-cols-[240px_1fr]">
            {/* Sidebar */}
            <aside className="border-b border-border md:border-b-0 md:border-r">
              <ScrollArea className="h-full max-h-[180px] md:max-h-none">
                <ul className="p-2">
                  {RESEARCH.map((r) => {
                    const isActive = r.slug === active;
                    return (
                      <li key={r.slug}>
                        <button
                          type="button"
                          onClick={() => setActive(r.slug)}
                          className={cn(
                            "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition",
                            isActive
                              ? "bg-brand-soft text-brand-deep"
                              : "text-brand-deep/70 hover:bg-brand-soft/50",
                          )}
                        >
                          <span className={cn("h-2 w-2 flex-shrink-0 rounded-full", SKU_DOT[r.sku])} />
                          <span className="flex-1 text-[13px] font-semibold leading-tight">
                            {r.name}
                          </span>
                          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-brand-deep/40">
                            {r.studyCount.replace(" studies", "")}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </aside>

            {/* Detail */}
            <ScrollArea className="h-full">
              <div className="px-6 py-6 md:px-8 md:py-8">
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-brand">
                  <span className={cn("h-1.5 w-1.5 rounded-full", SKU_DOT[ingredient.sku])} />
                  In · {ingredient.skuLabel} · {ingredient.studyCount}
                </div>
                <h3 className="mt-2 font-display text-2xl font-bold text-brand-deep md:text-3xl">
                  {ingredient.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {ingredient.summary}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <a
                    href={ingredient.pdf}
                    download
                    className="inline-flex items-center gap-2 rounded-full bg-brand-deep px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white shadow-[0_10px_30px_-12px_rgba(0,0,0,0.45)] transition hover:-translate-y-0.5"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download PDF
                  </a>
                  <a
                    href={`https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(ingredient.name)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-brand hover:underline"
                  >
                    Search all on PubMed
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  {ingredient.webmd && (
                    <a
                      href={ingredient.webmd}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-brand hover:underline"
                    >
                      WebMD overview
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {ingredient.clinicalTrials && (
                    <a
                      href={ingredient.clinicalTrials}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-brand hover:underline"
                    >
                      ClinicalTrials.gov
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {ingredient.medrxiv && (
                    <a
                      href={ingredient.medrxiv}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-brand hover:underline"
                    >
                      medRxiv preprints
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {ingredient.biorxiv && (
                    <a
                      href={ingredient.biorxiv}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-brand hover:underline"
                    >
                      bioRxiv preprints
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>

                <div className="mt-7 rounded-2xl border border-border bg-brand-soft/40 p-4">
                  <div className="flex items-start gap-3">
                    <Microscope className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" />
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">
                        Lead Researcher
                      </p>
                      <p className="mt-1 font-display text-base font-bold text-brand-deep">
                        {ingredient.scientist}
                      </p>
                      <p className="text-xs text-muted-foreground">{ingredient.affiliation}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-7">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand">
                    Selected Citations
                  </p>
                  <ol className="mt-3 space-y-3">
                    {ingredient.citations.map((c, i) => (
                      <li
                        key={c.url}
                        className="rounded-xl border border-border bg-white p-4 transition hover:border-brand/40"
                      >
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noreferrer"
                          className="group block"
                        >
                          <div className="flex items-start gap-3">
                            <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-soft text-[10px] font-bold text-brand-deep">
                              {i + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold leading-snug text-brand-deep group-hover:text-brand">
                                {c.title}
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                <span className="italic">{c.journal}</span> · {c.year}
                              </p>
                              <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-brand">
                                {new URL(c.url).hostname.replace("www.", "")}
                                <ExternalLink className="h-3 w-3" />
                              </p>
                            </div>
                          </div>
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="mt-8 flex items-start gap-2 rounded-xl border border-border/60 bg-white p-4 text-[11px] text-muted-foreground">
                  <FileText className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand" />
                  <p>
                    Citations curated from PubMed, WebMD, ClinicalTrials.gov, and the medRxiv /
                    bioRxiv preprint servers for the latest pre-publication research. Educational
                    use only, not medical advice.
                  </p>
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ResearchTriggerBand() {
  return (
    <ResearchDrawer>
      <button
        type="button"
        className="group mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-full border border-brand/15 bg-gradient-to-r from-brand-soft/60 via-white to-brand-soft/60 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-deep/80 shadow-sm transition hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
      >
        <span className="flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5 text-brand" strokeWidth={1.75} />
          10,000+ Published Studies Backing Key Ingredients
        </span>
        <span className="hidden items-center gap-1 text-brand sm:flex">
          Open library
          <ExternalLink className="h-3 w-3" />
        </span>
      </button>
    </ResearchDrawer>
  );
}
